import React from 'react';

import { PlatformExecuteAdHocQueryTeam200 } from '@cloudquery/api-platform';

import { TableData, BarChartData, PieChartData, XYChartGridData } from '../types';

export const defaultColumnFormatter = (label: unknown) => String(label ?? '').replaceAll('_', ' ');

export const mapLabelData = (
  queryResult: PlatformExecuteAdHocQueryTeam200,
  columnFormatter?: (label: string) => string,
): number | string =>
  (columnFormatter?.(queryResult.data?.rows?.[0]?.[0] as string) ??
    (queryResult.data?.rows?.[0]?.[0] || 0)) as string | number;

export const mapBarChartData = (
  queryResult: PlatformExecuteAdHocQueryTeam200,
  columnFormatter: (label: string) => string = defaultColumnFormatter,
): BarChartData =>
  [
    ...queryResult.data.rows,
    // @TODO: nice for testing larger data sets until the BE is fully seeded
    // ...Array(10)
    //   .fill(0)
    //   .map((_, i) => [`name-${i}`, (i + 1) * 100]),
  ].map((row: any) => ({
    name: columnFormatter?.(row[0]) ?? row[0],
    value: row[1],
  }));

export const mapPieChartData = (
  queryResult: PlatformExecuteAdHocQueryTeam200,
  columnFormatter: (label: string) => string = defaultColumnFormatter,
): PieChartData =>
  [
    ...queryResult.data.rows,
    // @TODO: nice for testing larger data sets until the BE is fully seeded
    // ...Array(10)
    //   .fill(0)
    //   .map((_, i) => [`name-${i}`, (i + 1) * 100]),
  ].map((row: any) => ({
    label: columnFormatter?.(row[0]) ?? row[0],
    name: `${row[0]}`,
    value: Number(row[1]),
  }));

const formatValue = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);

  return value;
};

export const mapTableData = (
  queryResult: PlatformExecuteAdHocQueryTeam200,
  columnFormatter: (label: string) => string = defaultColumnFormatter,
  cellFormatter?: (cell: any, colIdx: number) => React.ReactNode,
): TableData => {
  // Keep track of columns we've seen already
  const uniqueColumns: TableData['columns'] = [];
  const uniqueColumnIndices: number[] = [];

  // Process columns, only keeping unique ones (first across the line)
  for (let index = 0; index < queryResult.data.columns.length; index++) {
    const column = queryResult.data.columns[index];
    if (!uniqueColumns.some((c) => c.name === column.name)) {
      uniqueColumns.push({ label: String(columnFormatter(column.name)), name: column.name });
      uniqueColumnIndices.push(index);
    }
  }

  // Map rows to only include data from unique columns
  const mappedRows = queryResult.data.rows.map((row: any[]) =>
    uniqueColumnIndices.map((index) => {
      const value = row[index];

      // note that cellFormatter can return a JSX element such as the <TagsDisplay /> component
      return cellFormatter?.(value, index) ?? formatValue(value);
    }),
  );

  return {
    columns: uniqueColumns,
    rows: mappedRows,
  };
};

export const mapXYChartData = (queryResult: PlatformExecuteAdHocQueryTeam200): XYChartGridData => {
  return (
    queryResult?.data?.rows.map((row: any) => ({
      x: row[0] as Date,
      y: Number(row[1]),
    })) ?? []
  );
};
